"use client";

import { useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { ECON_CHART_STRINGS } from "@/components/tools/econ-micro/chart-strings";
import { TradeGraph } from "@/components/tools/econ-micro/trade-graph";
import { TradeView } from "@/components/tools/econ-micro/trade-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type Analysis,
  analyse,
  fromHours,
  gainsAt,
  type Producer,
} from "@/lib/tool-kit/econ-micro/trade";

/** English strings. A translated content file overrides these through `ui`. */
export const COMPARATIVE_ADVANTAGE_CALCULATOR_STRINGS = {
  ...ECON_CHART_STRINGS,
  formLabel: "Your data give",
  formOutput: "The most each can make of each good in a period",
  formHours: "The hours each needs to make one unit",
  namesHeading: "Producers and goods",
  producerA: "First producer",
  producerB: "Second producer",
  goodOne: "First good",
  goodTwo: "Second good",
  defaultProducerA: "Country A",
  defaultProducerB: "Country B",
  defaultGoodOne: "wheat",
  defaultGoodTwo: "cloth",
  dataHeading: "The numbers",
  outputLabel: "{producer}: most {good} in a period",
  hoursLabel: "{producer}: hours to make one unit of {good}",
  hoursAvailable: "Hours available to each producer",
  priceLabel: "Terms of trade (optional): units of {good2} per unit of {good1}",
  priceHint: "Leave blank to use the middle of the range.",
  results: "Results",
  invalidInput: "Enter every number as a value above 0.",
  costHeading: "Opportunity costs",
  producerColumn: "Producer",
  costColumn: "Opportunity cost of 1 {good} (in {other})",
  workOutput:
    "{producer}: 1 {good1} costs {y} ÷ {x} = {cost1} {good2}; 1 {good2} costs {x} ÷ {y} = {cost2} {good1}",
  workHours:
    "{producer}: 1 {good1} costs {t1} ÷ {t2} = {cost1} {good2}; 1 {good2} costs {t2} ÷ {t1} = {cost2} {good1}",
  absoluteIn: "Absolute advantage in {good}",
  comparativeIn: "Comparative advantage in {good}",
  neither: "Neither (equal)",
  advantageNote:
    "Absolute advantage means making more with the same resources. Comparative advantage means giving up less of the other good, so a producer can have the first in both goods but the second in only one.",
  tradeHeading: "Gains from trade",
  rangeGood1:
    "Trade helps both sides when 1 {good1} sells for more than {low} {good2} and less than {high} {good2}.",
  rangeGood2:
    "Seen from the other side, 1 {good2} should trade for between {low} and {high} {good1}.",
  gainsAt:
    "At {price} {good2} per {good1}: {s1} gains {gain1} {good2} for every {good1} it sells, and {s2} saves {gain2} {good2} for every {good1} it buys.",
  priceOutside:
    "A price of {price} is not between {low} and {high}, so at least one side gains nothing and would not agree to trade.",
  totalGain:
    "Together the two gain {gain} {good2} for every unit of {good1} traded, whatever price they settle on; the price only decides who gets how much of it.",
  noGains:
    "Both producers give up the same amount of one good for the other, so neither has a comparative advantage and trade creates no gains.",
  graphTitle: "{producer}: production possibilities and trade",
  curvePpf: "PPF",
  curveTrade: "With trade",
  pointSpecialize: "Specialize",
  tableCurve: "Curve",
  graphDescription:
    "Production possibilities frontier for {producer}: a straight line from {y} units of {good2} on the vertical axis to {x} units of {good1} on the horizontal axis. A dashed line shows what the producer can consume after specializing and trading.",
  graphNote:
    "The dashed line starts where the producer puts every resource into its better good and shows what it can consume by trading at the chosen price. It lies outside the frontier whenever that price beats the producer’s own opportunity cost.",
} as const;

export type ComparativeAdvantageStrings =
  typeof COMPARATIVE_ADVANTAGE_CALCULATOR_STRINGS;
type Form = "output" | "hours";

interface Inputs {
  a1: string;
  a2: string;
  b1: string;
  b2: string;
}

const START: Record<Form, Inputs> = {
  output: { a1: "60", a2: "60", b1: "20", b2: "40" },
  hours: { a1: "1", a2: "1", b1: "3", b2: "1.5" },
};

/** The middle of the mutually beneficial range, or null when there is none. */
function middle(analysis: Analysis | null): number | null {
  if (!analysis || analysis.rangeLow === null || analysis.rangeHigh === null) {
    return null;
  }
  return (analysis.rangeLow + analysis.rangeHigh) / 2;
}

export default function ComparativeAdvantageCalculatorWidget() {
  const strings = useToolUi(COMPARATIVE_ADVANTAGE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [form, setForm] = useState<Form>("output");
  const [inputs, setInputs] = useState(START);
  const [names, setNames] = useState<[string, string, string, string]>([
    strings.defaultProducerA,
    strings.defaultProducerB,
    strings.defaultGoodOne,
    strings.defaultGoodTwo,
  ]);
  const [hoursText, setHoursText] = useState("40");
  const [priceText, setPriceText] = useState("");
  const raw = inputs[form];
  const setNumber = (key: keyof Inputs) => (value: string) =>
    setInputs((all) => ({ ...all, [form]: { ...all[form], [key]: value } }));
  const setName = (index: number) => (value: string) =>
    setNames((all) => {
      const next: [string, string, string, string] = [...all];
      next[index] = value;
      return next;
    });
  const label = (value: string, fallback: string) =>
    value.trim() === "" ? fallback : value.trim();
  const producerNames: [string, string] = [
    label(names[0], strings.defaultProducerA),
    label(names[1], strings.defaultProducerB),
  ];
  const goods: [string, string] = [
    label(names[2], strings.defaultGoodOne),
    label(names[3], strings.defaultGoodTwo),
  ];

  const values = [raw.a1, raw.a2, raw.b1, raw.b2].map(parseNumberInput);
  const available = parseNumberInput(hoursText);
  const valid =
    values.every((v) => v !== null && v > 0) &&
    (form === "output" || (available !== null && available > 0));
  let producers: [Producer, Producer] | null = null;
  let hours: [[number, number], [number, number]] | null = null;
  if (valid) {
    const [a1, a2, b1, b2] = values as number[];
    if (form === "output") {
      producers = [
        { good1: a1, good2: a2 },
        { good1: b1, good2: b2 },
      ];
    } else {
      const total = available as number;
      const scale = (p: Producer): Producer => ({
        good1: p.good1 * total,
        good2: p.good2 * total,
      });
      producers = [scale(fromHours(a1, a2)), scale(fromHours(b1, b2))];
      hours = [
        [a1, a2],
        [b1, b2],
      ];
    }
  }
  const analysis = producers ? analyse(producers[0], producers[1]) : null;
  const typedPrice = parseNumberInput(priceText);
  const price =
    typedPrice !== null && typedPrice > 0 ? typedPrice : middle(analysis);
  const gains = analysis && price !== null ? gainsAt(analysis, price) : null;
  const first = form === "output" ? strings.outputLabel : strings.hoursLabel;
  const field = (id: string, key: keyof Inputs, who: number, good: number) => (
    <TextField
      id={id}
      key={id}
      label={t(first, { producer: producerNames[who], good: goods[good] })}
      numeric
      onChange={setNumber(key)}
      value={raw[key]}
    />
  );

  return (
    <div className="space-y-6">
      <SelectField
        id="ca-form"
        label={strings.formLabel}
        onChange={setForm}
        options={[
          { value: "output", label: strings.formOutput },
          { value: "hours", label: strings.formHours },
        ]}
        value={form}
      />
      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.namesHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="ca-pa"
            label={strings.producerA}
            onChange={setName(0)}
            value={names[0]}
          />
          <TextField
            id="ca-pb"
            label={strings.producerB}
            onChange={setName(1)}
            value={names[1]}
          />
          <TextField
            id="ca-g1"
            label={strings.goodOne}
            onChange={setName(2)}
            value={names[2]}
          />
          <TextField
            id="ca-g2"
            label={strings.goodTwo}
            onChange={setName(3)}
            value={names[3]}
          />
        </div>
      </section>
      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.dataHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {field("ca-a1", "a1", 0, 0)}
          {field("ca-a2", "a2", 0, 1)}
          {field("ca-b1", "b1", 1, 0)}
          {field("ca-b2", "b2", 1, 1)}
        </div>
        {form === "hours" ? (
          <div className="max-w-xs">
            <TextField
              id="ca-hours"
              label={strings.hoursAvailable}
              numeric
              onChange={setHoursText}
              value={hoursText}
            />
          </div>
        ) : null}
        <div className="max-w-md">
          <TextField
            hint={strings.priceHint}
            id="ca-price"
            label={t(strings.priceLabel, { good1: goods[0], good2: goods[1] })}
            numeric
            onChange={setPriceText}
            value={priceText}
          />
        </div>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {producers && analysis ? (
          <>
            <TradeView
              analysis={analysis}
              fmt={fmt}
              gains={gains}
              goods={goods}
              hours={hours}
              names={producerNames}
              price={price ?? 0}
              producers={producers}
              strings={strings}
            />
            {producers.map((producer, i) => {
              let specializes: boolean | null = null;
              if (analysis.gainsFromTrade) {
                specializes = analysis.comparativeGood1 === i;
              }
              return (
                <TradeGraph
                  fmt={fmt}
                  good1={goods[0]}
                  good2={goods[1]}
                  index={i}
                  key={producerNames[i] + String(i)}
                  name={producerNames[i]}
                  price={price}
                  producer={producer}
                  specializesIn1={specializes}
                  strings={strings}
                />
              );
            })}
            <p className="text-muted-foreground text-xs">{strings.graphNote}</p>
          </>
        ) : (
          <ErrorNotice>{strings.invalidInput}</ErrorNotice>
        )}
      </section>
    </div>
  );
}
